// „Zu tun“ in Worten (E3c, S2; G18, G19) – reine Logik, ohne React: je
// Anlage die Knöpfe für die Schritte, die jetzt gehen, und ein Satz, was als
// Nächstes kommt. Welche Schritte das sind, rechnet der Kern (kette.ts,
// zuTunSchritte) – hier nur, wie sie heißen und wohin sie führen.
//
// Wohin ein Knopf führt: „Freigeben“ zur Freigabe wie bisher; alles andere in
// die Akte der Anlage, gleich an die Stelle (#/anlage/<id>/<stelle>), wo es
// erledigt wird – Bestellung (S3), Dachpartie und Bodenpartie (S4), der
// Montagetermin bei den Terminen.

import { schrittIn, zuTunSchritte, type ZuTunKarte, type ZuTunSchritt } from "@happy-energy/kern";
import type { AkteStelle } from "../navigation.ts";

/** Was auf dem Knopf steht – sagt, was er tut (G19). */
export const KNOPF_WORT: Record<ZuTunSchritt, string> = {
  termin: "Termin eintragen",
  freigeben: "Freigeben",
  bestellt: "Bestellen",
  dachpartie: "Dachpartie beauftragen",
  bodenpartie: "Bodenpartie einteilen",
};

/** Wohin in der Akte – je Schritt außer dem Freigeben. */
const STELLE: Record<Exclude<ZuTunSchritt, "freigeben">, AkteStelle> = {
  termin: "termin",
  bestellt: "bestellung",
  dachpartie: "dachpartie",
  bodenpartie: "bodenpartie",
};

/** Die drei nach dem Freigeben, als Hauptwort – für den Satz. */
const DING: Record<"bestellt" | "dachpartie" | "bodenpartie", string> = {
  bestellt: "Bestellung",
  dachpartie: "Dachpartie",
  bodenpartie: "Bodenpartie",
};

export interface ZuTunKnopf {
  schritt: ZuTunSchritt;
  wort: string;
  /** Die Adresse hinter dem # – „/freigabe/<id>“, „/anlage/<id>/bestellung“. */
  pfad: string;
  /** Warum der Knopf (wieder) da ist – „Termin geändert – Mail erneut öffnen“, „Unterlage erneut senden?“; null: einfach offen. */
  grund: string | null;
}

/** Die Knöpfe einer Karte – in der Reihenfolge der Kette. */
export function knoepfe(k: ZuTunKarte): ZuTunKnopf[] {
  return zuTunSchritte(k.kette).map((schritt) => {
    const s = schrittIn(k.kette, schritt);
    return {
      schritt,
      wort: KNOPF_WORT[schritt],
      pfad: schritt === "freigeben" ? `/freigabe/${k.aufnahmeId}` : `/anlage/${k.anlageId}/${STELLE[schritt]}`,
      grund: s.stand === "wieder-offen" ? s.grund : s.hinweis,
    };
  });
}

/** „Bestellung, Dachpartie und Bodenpartie“ – die deutsche Aufzählung. */
export function aufzaehlen(woerter: readonly string[]): string {
  if (woerter.length <= 1) return woerter.join("");
  return `${woerter.slice(0, -1).join(", ")} und ${woerter.at(-1)}`;
}

/**
 * Ein Satz, was als Nächstes kommt (G19: niemand erklärt es Manuel). Er
 * richtet sich nach dem, was jetzt geht – die Knöpfe darunter sagen dasselbe
 * als Handgriff.
 */
export function naechsterSatz(k: ZuTunKarte): string {
  const schritte = zuTunSchritte(k.kette);
  const danach = (["bestellt", "dachpartie", "bodenpartie"] as const).filter((s) => schritte.includes(s));
  if (schritte.includes("freigeben")) {
    return schritte.includes("termin")
      ? "Das Material ansehen und freigeben und den Montagetermin eintragen – danach kommen Bestellung, Dachpartie und Bodenpartie."
      : "Das Material ansehen und freigeben – danach kommen Bestellung, Dachpartie und Bodenpartie.";
  }
  if (schritte.includes("termin")) return "Freigegeben – als Nächstes den Montagetermin eintragen, dann gehen Bestellung, Dachpartie und Bodenpartie.";
  if (danach.length === 3 && danach.every((s) => schrittIn(k.kette, s).stand === "offen")) {
    return "Freigegeben – jetzt bestellen und die Partien beauftragen, in beliebiger Reihenfolge.";
  }
  if (danach.length > 0) return `Noch zu tun: ${aufzaehlen(danach.map((s) => DING[s]))}.`;
  if (k.hinweis === "bitte-ansehen") return "Nach der Freigabe kam etwas dazu – bitte in der Freigabe ansehen und „gesehen“ drücken.";
  return "Gerade nichts zu tun.";
}
